import { onViewLoad } from "jose_modern/on_view_load.js";
import { Spinner } from "~/jose_modern/spinner.js";
import { findPartial } from "~/jose_modern/replace_partial.js";
import { Confirm } from "~/jose_modern/confirm.js";
import { safeSubmitForm } from "~/jose_modern/auto_submit.js";
import { once } from "~/jose_modern/once.js";
import { scanHints } from "~/jose_modern/hint.js";
import { scan as scanUnsavedChanges } from "~/jose_modern/warn_unsaved_changes.js";

const ROW_FIELDS = ["quantity", "unit", "equivalent_quantity", "equivalent_unit"];

const addSearchParams = (url, params = {}) =>
  new URL(
    `${url.origin}${url.pathname}?${new URLSearchParams([
      ...Array.from(url.searchParams.entries()),
      ...Object.entries(params),
    ]).toString()}`
  );

const splitFields = (elements) => {
  // returns [visibleField, hiddenField]
  if (window.getComputedStyle(elements[0].closest("[data-form-container]")).display === "none") {
    return [elements[1], elements[0]];
  } else {
    return [elements[0], elements[1]];
  }
};

const updateLabel = async () => {
  // There are two forms on the ingredient#show page. This code grabs
  // the data from the visible form and updates the hidden one.
  const quantityFields = document.querySelectorAll("[data-quantity-field]");
  const [quantityEl, hiddenQuantityEl] = splitFields(quantityFields);

  const unitFields = document.querySelectorAll("[data-unit-field]");
  const [unitEl, hiddenUnitEl] = splitFields(unitFields);

  // Keep hidden fields in sync
  hiddenQuantityEl.value = quantityEl.value;
  hiddenUnitEl.selectedIndex = unitEl.selectedIndex;

  // And use the visible fields to update the nutrition label
  const params = {
    quantity: quantityEl.value,
    unit: unitEl.value,
  };
  const url = addSearchParams(new URL(document.location), params);

  Spinner.start(findPartial("ingredients/show"));
  const response = await fetch(url, { headers: { accept: "text/javascript" } });

  if (!(response.status >= 200 && response.status < 300)) {
    throw new Error(`bad HTTP response: ${response.status}`);
  }

  const result = await response.text();
  new Function(result)();
};

function cloneMeasureTemplate(templateId, form) {
  const clone = document.querySelector(templateId).children[0].cloneNode(true);
  const inputs = clone.querySelectorAll("input, select, label");
  const measureIndex = parseInt(form.dataset.nextIndex || "0", 10);
  form.dataset.nextIndex = measureIndex + 1;

  inputs.forEach((input) => {
    if (input.name) input.name = input.name.replace(/\[\d+\]/, `[${measureIndex}]`);
    if (input.id) input.id = input.id.replace(/_\d+_/, `_${measureIndex}_`);
    if (input.htmlFor) input.htmlFor = input.htmlFor.replace(/_\d+_/, `_${measureIndex}_`);
  });

  clone.dataset["template"] = false;
  return clone;
}

function requiredFieldElements(row) {
  return ROW_FIELDS.map((field) => row.querySelector(`[data-field="${field}"]`));
}

function rowSnapshot(row) {
  // Snapshot every field (including optional ones like volume_type) so edits to
  // them count as dirty.
  const fields = Array.from(row.querySelectorAll("[data-field]"));
  return JSON.stringify(fields.map((el) => el.value));
}

function isRowComplete(row) {
  return requiredFieldElements(row).every(
    (el) => el && el.value && el.value.toString().trim() !== ""
  );
}

function refreshSaveButton(row) {
  const button = row.querySelector(".custom-measure-save");
  if (!button) return;
  const dirty = rowSnapshot(row) !== row.dataset.initialSnapshot;
  const complete = isRowComplete(row);
  const enabled = dirty && complete;
  button.disabled = !enabled;
  button.classList.toggle("is-outlined", !enabled);

  const wrap = row.querySelector(".custom-measure-save-wrap");
  const tip = wrap?._tippy;
  if (!tip) return;

  if (!dirty) {
    tip.setContent("No changes to save");
    tip.enable();
  } else if (!complete) {
    tip.setContent("Fill in all fields to save");
    tip.enable();
  } else {
    tip.disable();
  }
}

function bindRow(row) {
  once(row, "custom-measure-row", () => {
    row.dataset.initialSnapshot = rowSnapshot(row);
    refreshSaveButton(row);

    row.querySelectorAll("input, select").forEach((input) => {
      if (input.type === "hidden") return;
      const events = input.tagName === "SELECT" ? ["change"] : ["input", "change"];
      events.forEach((ev) => input.addEventListener(ev, () => refreshSaveButton(row)));
    });
  });
}

function wireAddButton(buttonId, templateId, anchorId, form) {
  const button = document.querySelector(buttonId);
  if (!button) return;

  button.addEventListener("click", (event) => {
    event.preventDefault();
    const clone = cloneMeasureTemplate(templateId, form);
    document.querySelector(anchorId).appendChild(clone);
    scanHints(clone);
    bindRow(clone);
    scanUnsavedChanges(document);
  });
}

function bindCustomMeasures() {
  const form = document.querySelector("#custom-measures-form");
  if (!form) return;

  const existingRows = form.querySelectorAll(".custom-measure-row[data-template='false']");
  form.dataset.nextIndex = existingRows.length;

  existingRows.forEach((row) => bindRow(row));

  wireAddButton("#add-custom-measure", "#custom-measure-template", "#custom-measure-anchor", form);
  wireAddButton(
    "#add-volume-measure",
    "#custom-volume-measure-template",
    "#custom-volume-measure-anchor",
    form
  );

  form.addEventListener("click", async (event) => {
    const deleteButton = event.target.closest(".custom-measure-delete");
    if (!deleteButton) return;
    event.preventDefault();

    const row = deleteButton.closest(".custom-measure-row");
    const isSaved = row.querySelector('input[id$="_id"]')?.value !== "";

    if (isSaved) {
      let unit = row.querySelector('[data-field="unit"]')?.value?.trim();
      const suffix = row.querySelector('[data-field="volume_type"]')?.value?.trim();
      if (unit && suffix) unit = `${unit}, ${suffix}`;
      const label = unit ? `Remove "${unit}"?` : "Remove this custom unit?";
      const result = await Confirm.confirm(label, { confirm: "Remove", html: false });
      if (!result || !result.response) return;
    }

    row.remove();
    scanUnsavedChanges(document);

    if (isSaved) safeSubmitForm(form);
  });
}

onViewLoad("ingredients#show", () => {
  document.querySelectorAll("[data-options-field]").forEach((e) => {
    e.addEventListener("change", updateLabel);
  });

  bindCustomMeasures();

  const archiveModal = document.getElementById("archive-ingredient-modal");
  if (archiveModal) {
    archiveModal.addEventListener("archived", () => location.reload());
    archiveModal.addEventListener("restored", () => location.reload());
  }
});

document.addEventListener("partial:replace", (event) => {
  if (event.partial === "ingredients/custom_measures_form") {
    bindCustomMeasures();
  }
});
